import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { sb } from "@/lib/supabase";
import { Zap, MessageCircle, Check } from "lucide-react";

const DISCORD_URL = "https://discord.gg/kCvCeFsZ5y";
const KR_PER_EUR = 35;        // interná jednotka; balík 700 kr = 30 €
const MARGIN = 1.25;          // 25 % marža
const USD_TO_EUR = 0.92;
const TOK_IN = 800, TOK_OUT = 150; // priemerná správa

// Ceny OpenRouter (USD / 1M tokenov) — Euryale živá k 10.7.2026,
// Celeste/Lumimaid orientačné (bez aktívneho providera v čase zápisu)
const MODELS = [
  { id: "sao10k/l3.3-euryale-70b",           name: "Euryale 70B",  tag: "full NSFW", usdIn: 0.65,  usdOut: 0.75, live: true },
  { id: "nothingiisreal/mn-celeste-12b",      name: "Celeste 12B",  tag: "NSFW",      usdIn: 0.80,  usdOut: 1.20, live: false },
  { id: "neversleep/llama-3.1-lumimaid-70b",  name: "Lumimaid 70B", tag: "full NSFW", usdIn: 3.375, usdOut: 4.50, live: false },
];

const PACKAGES = [
  { eur: 30, kr: 700 },
  { eur: 40, kr: 1050, popular: true },
  { eur: 55, kr: 1800 },
];

function perMsgKr(m) {
  const usd = (TOK_IN / 1e6) * m.usdIn + (TOK_OUT / 1e6) * m.usdOut;
  return usd * USD_TO_EUR * KR_PER_EUR * MARGIN;
}
const fmtMsgs = (n) => (n >= 1000 ? `~${Math.round(n / 1000)} 000` : `~${Math.round(n)}`);

export default function Pricing() {
  const { user } = useAuth();
  const { lang } = useT();
  const sk = lang === "sk";
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const { data } = await sb.from("profiles").select("dash_credits").eq("id", user.id).maybeSingle();
      setCredits(Number(data?.dash_credits ?? 0));
    })();
  }, [user?.id]);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-10" data-testid="pricing-page">
      <div>
        <h1 className="font-serif text-4xl text-white">{sk ? "Cenník" : "Pricing"}</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          {sk
            ? "AI správy sa účtujú z kreditov podľa reálnej spotreby zvoleného modelu. Kredit je spoločný pre všetky modely."
            : "AI messages are billed from credits based on real usage of the selected model. Credits are shared across models."}
        </p>
      </div>

      {/* Aktuálny zostatok */}
      <div className="flex items-center gap-3 border border-white/10 bg-white/[0.03] px-5 py-4 rounded-none max-w-md" data-testid="pricing-balance">
        <Zap size={18} className="text-fuchsia-400" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/40">{sk ? "Tvoj zostatok" : "Your balance"}</div>
          <div className="text-xl text-white font-mono">{credits === null ? "…" : credits.toFixed(2)} kr</div>
        </div>
      </div>

      {/* Balíky */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 mb-4">{sk ? "Dobiť kredity" : "Top up credits"}</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {PACKAGES.map((p) => (
            <a
              key={p.eur}
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pkg-${p.eur}`}
              className={`block border p-6 rounded-none transition-all hover:-translate-y-0.5 ${
                p.popular ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
              }`}
            >
              {p.popular && (
                <div className="text-[9px] uppercase tracking-[0.25em] text-fuchsia-300 mb-2">{sk ? "Najobľúbenejší" : "Most popular"}</div>
              )}
              <div className="font-serif text-3xl text-white">{p.eur} €</div>
              <div className="text-white/60 text-sm mt-1 font-mono">{p.kr} kr</div>
              <div className="mt-4 flex items-center gap-2 text-xs text-fuchsia-300">
                <MessageCircle size={13} />
                {sk ? "Kúpiť cez Discord" : "Buy via Discord"}
              </div>
            </a>
          ))}
        </div>
        <p className="text-white/35 text-xs mt-3">
          {sk
            ? "Po kliknutí ťa presmerujeme na náš Discord — napíš nám a kredity ti pridelíme do hodiny."
            : "You'll be redirected to our Discord — message us and we'll add your credits within an hour."}
        </p>
      </div>

      {/* Modely */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 mb-4">{sk ? "AI modely a spotreba" : "AI models & usage"}</div>
        <div className="border border-white/10 rounded-none overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
                <th className="px-4 py-3">Model</th>
                <th className="px-4 py-3">{sk ? "Cena / správa" : "Price / message"}</th>
                <th className="px-4 py-3">{sk ? "Správ za 30 €" : "Msgs per €30"}</th>
                <th className="px-4 py-3">{sk ? "Správ za 30 €" : "Msgs per €30"}</th>
                <th className="px-4 py-3">{sk ? "Správ za 50 €" : "Msgs per €50"}</th>
              </tr>
            </thead>
            <tbody>
              {MODELS.map((m) => {
                const pm = perMsgKr(m);
                return (
                  <tr key={m.id} className="border-b border-white/5 last:border-0" data-testid={`model-row-${m.name.replace(/\s/g, "")}`}>
                    <td className="px-4 py-3">
                      <div className="text-white">{m.name}</div>
                      <div className="text-[10px] text-white/40 flex items-center gap-1">
                        <Check size={10} className="text-emerald-400" /> {m.tag}
                        {!m.live && <span className="text-amber-400/70 ml-2">{sk ? "· orientačná cena" : "· indicative price"}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-white/80">{pm.toFixed(3)} kr</td>
                    <td className="px-4 py-3 font-mono text-white/60">{fmtMsgs(700 / pm)}</td>
                    <td className="px-4 py-3 font-mono text-white/60">{fmtMsgs(1050 / pm)}</td>
                    <td className="px-4 py-3 font-mono text-white/60">{fmtMsgs(1750 / pm)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-white/35 text-xs mt-3">
          {sk
            ? `Prepočet vychádza z priemernej správy (~${TOK_IN} tokenov kontext + ~${TOK_OUT} tokenov odpoveď). Skutočná cena sa účtuje podľa reálnych tokenov každej správy.`
            : `Estimates based on an average message (~${TOK_IN} tokens context + ~${TOK_OUT} tokens reply). Actual cost is billed per real tokens of each message.`}
        </p>
      </div>
    </div>
  );
}
